<template>
  <div
    class="outreach-contact-stats__header"
    :style="statStyles"
  >
    <StreamPicker
      v-if="hasMediaMonitoring"
      v-model:filter="streamPickerFilter"
      class="outreach-contact-stats__stream-picker"
      :clearable="Boolean(stream.id || filter)"
      keyword-search
      :model-value="stream.id"
      placeholder-text="Filter by Mention Stream"
      single
      @update:model-value="$emit('update:streamId', $event)"
    />
    <BaseInputDatePicker
      action-button-label="Apply"
      bordered
      close-on-update
      limit-to-past-year
      placement="bottom-end"
      :range="filters.range"
      range-selection="custom"
      show-date-input
      show-range-options
      show-time-input
      show-update-button
      @update:range="$emit('update:range', $event)"
    />
  </div>

  <div class="outreach-contact-stats">
    <div
      v-if="!hideStats"
      class="outreach-contact-stats__body"
    >
      <div class="outreach-contact-stats__chart">
        <MentionVolumeWidget
          v-if="stream.id"
          :authors="filters.authors"
          :media="filters.media"
          :range="filters.range"
          :selected-stream="stream"
        />
        <OutreachContactRecentArticlesMentionVolume
          v-else
          :filters="filters"
          :stream="stream"
        />
      </div>
      <div class="outreach-contact-stats__totals">
        <template v-if="loading">
          <div class="outreach-contact-stats__loader">
            <QSpinner :size="30" />
          </div>
        </template>
        <template v-else>
          <div class="outreach-contact-stats__count">
            <div class="outreach-contact-stats__count-title">
              {{
                $t(
                  "outreach_contact_recent_articles_stats.total_mentions_title"
                )
              }}
            </div>
            <div class="outreach-contact-stats__count-value">
              {{ prettyNumber(totalDocCount) }}
            </div>
          </div>
          <div class="outreach-contact-stats__media">
            <div
              v-for="period in currentPeriodMentions"
              :key="period.key"
              class="outreach-contact-stats__count"
            >
              <div
                class="outreach-contact-stats__count-title outreach-contact-stats__count-media print"
                :class="period.key.toLowerCase()"
              >
                {{ period.key }}
              </div>
              <div
                v-if="currentPeriodMentions.length > 1"
                class="outreach-contact-stats__count-value"
              >
                {{ prettyNumber(period.doc_count) }}
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { BaseInputDatePicker } from "shared/components/base";
import StreamPicker from "shared/components/streams/pickers/StreamPicker";
import MentionVolumeWidget from "shared/components/widgets/MentionVolumeWidget.vue";
import { prettyNumber } from "shared/helpers/number";
import StreamFilters from "shared/helpers/StreamFilters";
import StreamService from "shared/services/api/StreamService";

import OutreachContactRecentArticlesMentionVolume from "./OutreachContactRecentArticlesMentionVolume.vue";

export default {
  name: "OutreachContactRecentArticlesStats",
  components: {
    StreamPicker,
    MentionVolumeWidget,
    BaseInputDatePicker,
    OutreachContactRecentArticlesMentionVolume,
  },
  props: {
    stream: {
      type: Object,
      required: true,
    },
    filters: {
      type: StreamFilters,
      required: true,
    },
    hideStats: {
      type: Boolean,
    },
    sticky: {
      type: Boolean,
    },
    offsetHeight: {
      type: Number,
      default: 0,
    },
  },
  emits: ["update:streamId", "update:range", "update:filter"],
  data() {
    return {
      loaded: false,
      loading: false,
      currentPeriodMentions: [],
      filter: "",
    };
  },
  computed: {
    streamPickerFilter: {
      get() {
        return this.filter;
      },
      set(value) {
        this.filter = value;
        this.$emit("update:filter", value);
      },
    },
    totalDocCount() {
      return this.currentPeriodMentions
        .map((source) => source.doc_count)
        .reduce((acc, count) => acc + count, 0);
    },
    hasMediaMonitoring() {
      return this.$features.has("has_media_platform");
    },
    statStyles() {
      if (this.sticky) {
        return {
          backgroundColor: "white",
          position: "sticky",
          top: `${this.offsetHeight}px`,
          zIndex: 10,
        };
      }

      return {};
    },
  },
  watch: {
    stream: {
      deep: true,
      handler() {
        this.reload();
      },
    },
    filters: {
      deep: true,
      handler() {
        this.reload();
      },
    },
  },
  mounted() {
    this.reload();
  },
  methods: {
    prettyNumber,
    reload() {
      this.loaded = false;
      this.loadAggregateData();
    },
    async loadAggregateData() {
      if (this.loaded) return;

      try {
        this.error = false;
        this.loading = true;

        const params = {
          after: this.filters.range.after,
          before: this.filters.range.before,
          media: this.filters.media,
          field: "medium",
          authors: this.filters.authors.join(","),
          query: this.stream.keywords,
          keyword: this.stream.keyword,
        };

        const response = this.stream.id
          ? await StreamService.aggregate(this.stream.id, {
              params,
            })
          : await this.$streemApiV1.post(
              `influencers/${this.stream.influencer_id}/recent_articles_aggregate`,
              {
                params,
              }
            );

        this.currentPeriodMentions = response.data;
      } catch {
        this.error = true;
      } finally {
        this.loaded = true;
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.outreach-contact-stats {
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 1px solid var(--s-color-denim-2);
  border-radius: 6px;

  &__chart {
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-lg);
    flex: 1;
    justify-content: flex-end;
  }

  &__header {
    display: flex;
    padding-bottom: 16px;
    padding-top: 24px;
    background-color: white;
    justify-content: space-between;
  }

  &__body {
    display: flex;
    padding: var(--s-spacing-md);
    gap: var(--s-spacing-lg);
    min-height: 120px;
  }

  &__stream-picker {
    width: 300px;
  }

  &__date-picker-button {
    justify-content: flex-start;
    font-weight: var(--s-font-weight-bold);

    .chevron {
      transition: transform 0.1s ease;

      &.rotate {
        transform: rotate(180deg);
      }
    }
  }

  &__totals {
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-md);
    flex-shrink: 0;
    width: 170px;
    justify-content: center;
  }

  &__media {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--s-spacing-lg);
  }

  &__count {
    display: flex;
    flex-direction: column;
    gap: var(--s-spacing-xs);
    white-space: nowrap;

    &-title {
      font-weight: var(--s-font-weight-bold);
    }

    &-media {
      --media-color: var(--s-color-denim-2);

      position: relative;
      padding-left: 14px;

      &::before {
        content: "";
        background-color: var(--media-color);
        width: 10px;
        height: 10px;
        border-radius: var(--s-border-radius-sm);
        position: absolute;
        left: 0;
        top: 50%;
        margin-top: -5px;
      }

      &.print {
        --media-color: var(--s-color-media-print);
      }

      &.online {
        --media-color: var(--s-color-media-online);
      }

      &.magazine {
        --media-color: var(--s-color-media-magazine);
      }

      &.podcast {
        --media-color: var(--s-color-media-podcast);
      }

      &.radio {
        --media-color: var(--s-color-media-radio);
      }

      &.social {
        --media-color: var(--s-color-media-social);
      }

      &.tv {
        --media-color: var(--s-color-media-tv);
      }
    }

    &-value {
      font-weight: var(--s-font-weight-bold);
      font-size: var(--s-font-size-400);
    }
  }

  &__loader {
    display: flex;
    flex: 1;
    justify-content: center;
    align-items: center;
  }
}
</style>
